import { defaultWebStudioScope, type WebStudioScope } from "../api/boot";

/**
 * URL search params recognized by Web Studio. The URL is a non-authoritative
 * deep-link surface: values are forwarded to the backend, which owns route
 * validation, authorization, and redaction. Unknown keys are dropped so the
 * browser never accumulates an unreviewed public config surface.
 */
export interface WebStudioScopeSearch {
  account_id?: string | undefined;
  organization_id?: string | undefined;
  project_id?: string | undefined;
  workspace_id?: string | undefined;
  environment_id?: string | undefined;
  worldview_id?: string | undefined;
  lineage_id?: string | undefined;
  activation_id?: string | undefined;
  shellReference?: string | undefined;
  shell_reference?: string | undefined;
  inspectorContext?: string | undefined;
  inspector_context?: string | undefined;
  activityLens?: string | undefined;
  activity_lens?: string | undefined;
  replaySubMode?: string | undefined;
  replay_sub_mode?: string | undefined;
  exportKind?: string | undefined;
  export_kind?: string | undefined;
  provenanceSeedId?: string | undefined;
  provenance_seed_id?: string | undefined;
  commandId?: string | undefined;
  command_id?: string | undefined;
  supportSnapshotId?: string | undefined;
  support_snapshot_id?: string | undefined;
  mutationContextDigest?: string | undefined;
  mutation_context_digest?: string | undefined;
}

const SCOPE_SEARCH_KEYS = [
  "account_id",
  "organization_id",
  "project_id",
  "workspace_id",
  "environment_id",
  "worldview_id",
  "lineage_id",
  "activation_id",
  "shellReference",
  "shell_reference",
  "inspectorContext",
  "inspector_context",
  "activityLens",
  "activity_lens",
  "replaySubMode",
  "replay_sub_mode",
  "exportKind",
  "export_kind",
  "provenanceSeedId",
  "provenance_seed_id",
  "commandId",
  "command_id",
  "supportSnapshotId",
  "support_snapshot_id",
  "mutationContextDigest",
  "mutation_context_digest",
] as const satisfies readonly (keyof WebStudioScopeSearch)[];

export function validateScopeSearch(input: Record<string, unknown>): WebStudioScopeSearch {
  const search: WebStudioScopeSearch = {};
  for (const key of SCOPE_SEARCH_KEYS) {
    const value = input[key];
    if (typeof value === "string" && value !== "") {
      search[key] = value;
    }
  }
  return search;
}

export function scopeFromSearch(search: WebStudioScopeSearch): WebStudioScope {
  const fallback = defaultWebStudioScope();
  return {
    accountId: search.account_id ?? fallback.accountId,
    organizationId: search.organization_id ?? fallback.organizationId,
    projectId: search.project_id ?? fallback.projectId,
    workspaceId: search.workspace_id ?? fallback.workspaceId,
    environmentId: search.environment_id ?? fallback.environmentId,
    worldviewId: search.worldview_id ?? fallback.worldviewId,
    lineageId: search.lineage_id ?? fallback.lineageId,
    activationId: search.activation_id ?? fallback.activationId,
    shellReference: search.shellReference ?? search.shell_reference,
    inspectorContext: search.inspectorContext ?? search.inspector_context,
    activityLens: search.activityLens ?? search.activity_lens,
    replaySubMode: search.replaySubMode ?? search.replay_sub_mode,
    exportKind: search.exportKind ?? search.export_kind,
    provenanceSeedId: search.provenanceSeedId ?? search.provenance_seed_id,
    commandId: search.commandId ?? search.command_id,
    supportSnapshotId: search.supportSnapshotId ?? search.support_snapshot_id,
    mutationContextDigest: search.mutationContextDigest ?? search.mutation_context_digest,
  };
}

/** Scope fields editable from the shell top bar, written back as URL state. */
export type ScopeSearchField = "organization_id" | "workspace_id" | "environment_id";
